import { SGRQ_DOMAIN_LABELS, type SGRQDomainKey, type SGRQResultPayload } from '../tests/SGRQTest';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import QuestionnaireResultHero from '../components/QuestionnaireResultHero';

export const getSGRQSeverityLabel = (total?: number | null): string => {
  if (typeof total !== 'number') return '—';
  return 'Escore SGRQ (0–100)';
};

export const getSGRQSeverityTone = (total?: number | null): string => {
  if (typeof total !== 'number') return 'bg-slate-100 text-slate-700';
  return 'bg-slate-100 text-slate-700';
};

export const getSGRQScoreColor = (total?: number | null): string => {
  if (typeof total !== 'number') return '#475569';
  return '#0f766e';
};

export const getSGRQInterpretationText = (
  total?: number | null,
  domains?: Partial<Record<SGRQDomainKey, number | null>> | null,
): string => {
  if (typeof total !== 'number') return 'Não há dados suficientes para interpretar o SGRQ.';

  const dominantDomain = (Object.entries(domains ?? {}) as Array<[SGRQDomainKey, number | null]>)
    .filter((entry): entry is [SGRQDomainKey, number] => typeof entry[1] === 'number')
    .sort((a, b) => b[1] - a[1])[0];

  const dominantText = dominantDomain
    ? `O maior comprometimento está em ${SGRQ_DOMAIN_LABELS[dominantDomain[0]].toLowerCase()}.`
    : '';

  return `O escore total foi ${total.toFixed(1)}/100; valores maiores representam pior qualidade de vida relacionada à saúde. ${dominantText} Não foi aplicada divisão automática em quartis de gravidade.`.trim();
};

interface SGRQResultRendererProps {
  payload: Partial<SGRQResultPayload> | null | undefined;
}

export default function SGRQResultRenderer({ payload }: SGRQResultRendererProps) {
  const total = typeof payload?.total === 'number' ? payload.total : null;
  const domains = {
    symptoms:
      typeof payload?.domains?.symptoms?.score === 'number'
        ? payload.domains.symptoms.score
        : typeof payload?.symptoms === 'number'
          ? payload.symptoms
          : null,
    activity:
      typeof payload?.domains?.activity?.score === 'number'
        ? payload.domains.activity.score
        : typeof payload?.activity === 'number'
          ? payload.activity
          : null,
    impact:
      typeof payload?.domains?.impact?.score === 'number'
        ? payload.domains.impact.score
        : typeof payload?.impact === 'number'
          ? payload.impact
          : null,
  } satisfies Record<SGRQDomainKey, number | null>;
  const interpretation = resolveAssessmentInterpretationText(payload, getSGRQInterpretationText(total, domains));
  const clinicalNotes = Array.isArray(payload?.clinicalNotes) ? payload.clinicalNotes : [];

  return (
    <div className="space-y-6">
      <QuestionnaireResultHero
        title="Pontuação SGRQ"
        value={total != null ? total.toFixed(1) : '—'}
        valueColor={getSGRQScoreColor(total)}
        valueCaption={typeof total === 'number' ? `${total.toFixed(1)}/100` : 'Pontuação indisponível'}
        classification={getSGRQSeverityLabel(total)}
        supportingText="Valores mais altos indicam maior impacto na qualidade de vida."
        interpretation={interpretation}
      />

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Domínio</th>
              <th className="px-6 py-4">Pontuação</th>
              <th className="px-6 py-4">Leitura clínica</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {(Object.keys(domains) as SGRQDomainKey[]).map((key) => (
              <tr key={key} className="hover:bg-slate-50/60">
                <td className="px-6 py-4 font-medium text-slate-700">{SGRQ_DOMAIN_LABELS[key]}</td>
                <td className="px-6 py-4 font-black text-slate-900">
                  {domains[key] != null ? `${domains[key]!.toFixed(1)}/100` : '—'}
                </td>
                <td className="px-6 py-4 text-slate-700">
                  {domains[key] == null ? 'Sem dados' : 'Maior pontuação = maior impacto'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {clinicalNotes.length > 0 && (
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-slate-400">Observações clínicas</div>
          <ul className="space-y-2 text-sm leading-relaxed text-slate-700">
            {clinicalNotes.map((item) => (
              <li key={item} className="rounded-xl bg-slate-50 px-4 py-3">
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
